/**
 * パスワードリセット要求ページ (Phase 3 — Figma 準拠).
 *
 * email → POST /auth/forgot-password → 送信完了表示
 * Figma: #14, #68 (送信完了)
 */

import { Link } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { authForgotPassword } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'パスワードリセット - PetaCMS';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authForgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(formatErrorMessage(err, '送信に失敗しました。もう一度お試しください'));
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
        <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <h2 className="mt-4 text-[16px] font-semibold text-[#23221e]">メールを送信しました</h2>
          <p className="mt-3 text-[14px] text-[#706d65]">
            <span className="font-medium text-[#23221e]">{email}</span>{' '}
            にパスワードリセット用のリンクを送信しました。
          </p>
          <p className="mt-2 text-[13px] text-[#706d65]">
            メールが届かない場合は、迷惑メールフォルダをご確認ください。
          </p>
          <div className="mt-6">
            <Link
              to="/login"
              className="text-[13px] font-medium text-[#3b82f6] hover:text-[#1d4ed8]"
            >
              ログインページに戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10">
        <div className="mb-8 text-center">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <p className="mt-1 text-[14px] text-[#706d65]">パスワードをリセット</p>
        </div>

        {error && (
          <div className="mb-5 rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-3 text-sm text-[#991b1b]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="mb-2 block text-[13px] font-medium text-[#23221e]">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] bg-white px-[14px] text-[14px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
            />
            <p className="mt-2 text-[13px] text-[#706d65]">
              登録メールアドレスにリセット用リンクを送信します
            </p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-[15px] font-semibold text-white transition hover:bg-[#1d4ed8] disabled:opacity-50"
          >
            {loading ? '送信中...' : 'リセットメールを送信'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-[13px] text-[#3b82f6] hover:text-[#1d4ed8]">
            ログインに戻る
          </Link>
        </div>
      </div>
    </div>
  );
}
